/**
 * Block 14 «Раскрой» — словарь видов обработки (PR #10b-2, CUT-3.3).
 *
 * Пиксельный порт `app.js:364-393` (`ProcessingDict`): localStorage-словарь видов
 * обработки детали («Склейка», «Фрезеровка» + пользовательские). Частые сверху —
 * сортировка по `useCount↓`, затем по имени (localeCompare 'ru'). Ключ хранилища —
 * канонический `pomoshnik_processing_v1` (storageKeys §0.4): это UI-настройка
 * пользователя (не ПДн/каталог), localStorage допустим (CLAUDE.md / P-007).
 *
 * @see Помощник — Прототип Раскроя/01-prototype-vanilla/js/app.js:364-393
 */

import { CUTTING_STORAGE_KEYS } from '../state/storageKeys';

/** Запись словаря обработки. */
export interface ProcessingEntry {
  name: string;
  isDefault: boolean;
  useCount: number;
}

const KEY = CUTTING_STORAGE_KEYS.processing;

/** Дефолтные виды обработки (вербатим прототипа app.js:366-369). */
const DEFAULTS: readonly ProcessingEntry[] = [
  { name: 'Склейка', isDefault: true, useCount: 0 },
  { name: 'Фрезеровка', isDefault: true, useCount: 0 },
];

/** Глубокая копия дефолтов (чтобы вызывающий не мутировал константу). */
function cloneDefaults(): ProcessingEntry[] {
  return DEFAULTS.map((d) => ({ ...d }));
}

/** Валиден ли разобранный элемент словаря (терпим к чужим/битым данным). */
function isEntry(v: unknown): v is ProcessingEntry {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as ProcessingEntry).name === 'string' &&
    (v as ProcessingEntry).name.trim() !== ''
  );
}

/**
 * Загрузить словарь из localStorage. При отсутствии — записать и вернуть дефолты.
 * При наличии — домержить недостающие дефолты (вербатим app.js:370-379). Любая
 * ошибка (нет localStorage / битый JSON) → дефолты (не крэш).
 */
function load(): ProcessingEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      const defaults = cloneDefaults();
      save(defaults);
      return defaults;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return cloneDefaults();
    const arr: ProcessingEntry[] = parsed.filter(isEntry).map((p) => ({
      name: p.name,
      isDefault: Boolean(p.isDefault),
      useCount: Number.isFinite(p.useCount) ? p.useCount : 0,
    }));
    const existing = new Set(arr.map((p) => p.name.toLowerCase()));
    for (const d of DEFAULTS) {
      if (!existing.has(d.name.toLowerCase())) arr.push({ ...d });
    }
    return arr;
  } catch {
    return cloneDefaults();
  }
}

/** Сохранить словарь в localStorage (молча игнорируем ошибки записи). */
function save(arr: ProcessingEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    /* localStorage недоступен (приватный режим / квота) — UI-словарь не критичен */
  }
}

/**
 * Все записи, отсортированные «частые сверху»: useCount↓, затем имя (ru).
 * Вербатим app.js:381-383.
 */
export function getAll(): ProcessingEntry[] {
  return load().sort(
    (a, b) => (b.useCount || 0) - (a.useCount || 0) || a.name.localeCompare(b.name, 'ru'),
  );
}

/**
 * Учесть применение вида обработки: существующий (case-insensitive) → +1 useCount,
 * новый → добавить `{ isDefault:false, useCount:1 }`. Пустая строка игнорируется.
 * Вербатим app.js:384-391.
 */
export function add(name: string): void {
  const trimmed = (name || '').trim();
  if (!trimmed) return;
  const arr = load();
  const existing = arr.find((p) => p.name.toLowerCase() === trimmed.toLowerCase());
  if (existing) existing.useCount = (existing.useCount || 0) + 1;
  else arr.push({ name: trimmed, isDefault: false, useCount: 1 });
  save(arr);
}

export const ProcessingDict = { getAll, add };
